import React from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import s from "./IconButton.module.scss"

/**
 * Icon-only button. `label` is required because an icon alone gives screen
 * readers nothing to announce.
 *
 * @example
 * <IconButton label="View row" variant="solid" onClick={open}><Eye size={15} /></IconButton>
 * <IconButton label="Filters" variant="outline" count={3} onClick={toggle}><Filter size={15} /></IconButton>
 */
const IconButton = React.forwardRef(function IconButton(
  {
    children,
    label,
    variant = "ghost",
    size = "md",
    isActive = false,
    count,
    className,
    type = "button",
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(s.iconBtn, s[size], isActive ? s.active : s[variant], className)}
      {...rest}
    >
      {children}
      {count > 0 && (
        <span className={s.badge} aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  )
})

IconButton.propTypes = {
  children: PropTypes.node.isRequired,
  /** Accessible name — announced by screen readers and shown as tooltip. */
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["solid", "outline", "ghost"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  isActive: PropTypes.bool,
  /** Optional count bubble, e.g. number of applied filters. */
  count: PropTypes.number,
  className: PropTypes.string,
  type: PropTypes.string
}

export default IconButton
